'use client';

import { useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { FindingsGroupByField } from '@terra/shared-contracts-findings';
import {
  terraProxyApiClient,
  type FindingsBulkFilter,
} from '../../clients/testerClient';
import {
  FINDINGS_COUNT_QUERY_KEY,
  serializeFindingsBulkFilterForCountQueryKey,
} from './findings-matching-count-query';

const resolveBrowserTimeZone = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone;

export const useFindingsGroupCounts = ({
  targetId,
  filter,
  groupBy,
}: {
  targetId: string | null | undefined;
  filter: FindingsBulkFilter;
  groupBy: FindingsGroupByField | null;
}) => {
  const timeZone = useMemo(resolveBrowserTimeZone, []);
  const query = useQuery({
    queryKey: [
      FINDINGS_COUNT_QUERY_KEY,
      'groups',
      targetId,
      groupBy,
      timeZone,
      serializeFindingsBulkFilterForCountQueryKey(filter),
    ],
    queryFn: () => {
      if (!targetId || !groupBy) {
        throw new Error('targetId and groupBy are required');
      }

      return terraProxyApiClient.countFindingsGroupsByFilter({
        payload: { applicationId: targetId, filter },
        groupBy,
        timeZone,
      });
    },
    enabled: !!targetId && !!groupBy,
    refetchOnWindowFocus: false,
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[3] === groupBy
        ? keepPreviousData(previousData)
        : undefined,
  });
  const countByKey = useMemo(
    () =>
      query.data
        ? new Map(query.data.map((group) => [group.key, group.count]))
        : undefined,
    [query.data],
  );

  return { groups: query.data, countByKey };
};
